/**
 * Shared text helpers for the consignee book search (Favoritos / Recientes).
 *
 * `normalizeSearchText` is applied to BOTH the query and every field value, so
 * the client-side filter is accent- and case-insensitive. NFD decomposition
 * maps "ñ" to "n" + U+0303 and stripping the combining marks leaves a plain
 * "n", so "peña" also matches "pena". That is accepted.
 *
 * `sanitizeFulltextQuery` is a port of the Django implementation (Leg 4): it
 * protects the SQL Server FULLTEXT CONTAINS predicate from characters that
 * would make it throw. Reserved characters are REPLACED by a space, never
 * deleted, so "O'Brien" becomes "O Brien" (what the word breaker does) instead
 * of the token-merging "OBrien".
 */

const COMBINING_MARKS = /[̀-ͯ]/g;
// The hyphen is not reserved by FULLTEXT, but the stored procedure drops any
// token containing one (`CHARINDEX('-', value) = 0`, verified 2026-09-12), so
// "V-12345678" reached it as a token it threw away, leaving an empty token set
// and the unfiltered book. Split on it instead and the digits become a real
// token that CONTAINS can match against the identification columns.
const FULLTEXT_RESERVED = /["'()[\]{}~*-]/g;
const SEARCH_PUNCTUATION = /[-.\s]/g;

export const normalizeSearchText = (value: string): string =>
  value.normalize("NFD").replace(COMBINING_MARKS, "").toLowerCase();

export const sanitizeFulltextQuery = (search: string): string =>
  search.replace(FULLTEXT_RESERVED, " ").replace(/\s+/g, " ").trim();

/**
 * Identification numbers and phones are matched punctuation-insensitively on
 * both sides, so the "V-12.345.678" the teller reads on screen matches whether
 * they type "v-12345678", "V12345678" or "12.345.678".
 */
export const stripSearchPunctuation = (value: string): string =>
  value.replace(SEARCH_PUNCTUATION, "");

/**
 * The exact string compared against `serverQuery` to decide whether a whole-book
 * server search still covers the text in the box. Sanitising first means
 * trailing or doubled spaces never break the comparison.
 */
export const toServerQueryForm = (search: string): string =>
  normalizeSearchText(sanitizeFulltextQuery(search));

/**
 * The tokens the stored procedure will actually search on. It drops anything
 * one character long (`LEN(RTRIM(LTRIM(value))) > 1`), and when nothing
 * survives it abandons the search entirely and returns the plain capped book —
 * so a query with no surviving token is an expensive call that answers with
 * what we already have cached.
 */
export const serverSearchTokens = (serverQueryForm: string): string[] =>
  serverQueryForm.split(" ").filter((token) => token.length > 1);

/**
 * Is a whole-book result fetched for `previous` still sound for `next`?
 *
 * The procedure joins its tokens with OR, not AND
 * (`STRING_AGG('"' + value + '*"', ' OR ')`, verified 2026-09-12), so ADDING a
 * token WIDENS the server's answer: a result fetched for "mar" is missing
 * everything that matches only "lopez", and reusing it would let the dropdown
 * claim "Sin coincidencias en su libreta" about someone who is in it.
 * Extending a token narrows the answer instead — `"maria*"` is a subset of
 * `"mar*"` — so that alone stays covered.
 */
export const serverQueryStillCovers = (
  previous: string,
  next: string
): boolean => {
  const before = serverSearchTokens(previous);
  const after = serverSearchTokens(next);

  if (before.length === 0 || before.length !== after.length) return false;

  return before.every((token, index) => after[index].startsWith(token));
};
